// pfs-card-header.ts — generischer Card-Header (PFS-595, Epic PFS-591).
//
// Loest das ueber alle Cut-Apps 6-10x kopierte .header-DOM-Muster ab.
// Layout (PFS-755): Icon-Kachel links, daneben Titel + Subtitle als Text-
// spalte (Media-Object), dann zwei FREIE Slots — links-inline + rechts.
// Die Card-Header trifft KEINE Fachannahmen mehr (kein data-badge/-count):
// Badges/Zaehler/Status/Aktionen sind Consumer-Content in den Slots.
// Die .card-Huelle bleibt die Foundation-.card(.stack); dieses WC ist NUR der
// Header-Block (ein direktes .stack-Kind der Card).
//
// Attribute (alle data-*):
//   data-title    Titel-Text (h2)
//   data-subtitle optionale gedaempfte Beschreibungszeile — direkt unter dem
//                 Titel in derselben Textspalte (rechts vom Icon).
// Der Divider/die Sektionierung ist KEINE Header-Eigenschaft mehr (PFS-756):
// die Card besitzt die Struktur (Header-Band ↔ Body-Zone) via <pfs-card
// data-sectioned>. Der Header rendert nur seinen Inhalt.
// Slots (alle optional, leer -> unsichtbar):
//   icon         Icon links (z.B. <svg slot="icon">) in gebrandeter Kachel.
//   header-left  inline, linksbuendig direkt hinter dem Text-Block — z.B.
//                <pfs-badge slot="header-left">…</pfs-badge> (App-Badge/Count).
//   header-right frei gestaltbare rechte Zone (ans Ende geschoben) — Aktionen,
//                Status, Toggles, was der Consumer braucht.
//
// Voll brandbar (nur --pfs-color-*-Tokens).
import { defineComponent } from "../../composition/define-component.js";
import { withShadow } from "../../composition/with-shadow.js";

const STYLES = `
:host { display: block; }
/* Icon · Textspalte · header-left · header-right — eine Achse, vertikal zentriert. */
.head { display: flex; align-items: center; gap: 0.7rem; }
/* icon-Slot: gebrandete Kachel links (opt-in; leer -> versteckt). */
.ico { display: grid; place-items: center; height: 38px; width: 38px; border-radius: 0.7rem; flex: none; color: var(--pfs-color-primary); background: color-mix(in srgb, var(--pfs-color-primary) 14%, transparent); }
.ico ::slotted(svg) { width: 20px; height: 20px; display: block; }
/* Textspalte: Titel + Subtitle untereinander (als Einheit neben dem Icon). */
.txt { display: flex; flex-direction: column; gap: 0.1rem; min-width: 0; }
.title { margin: 0; font-size: var(--pfs-font-size-card-title, 1.05rem); font-weight: 600; color: var(--pfs-color-on-surface); }
.title:empty { display: none; }
.subtitle { margin: 0; font-size: 0.9rem; color: var(--pfs-color-on-surface-muted); max-width: 60ch; }
.subtitle:empty { display: none; }
/* header-left: inline direkt hinter dem Text-Block. */
.hleft { display: flex; align-items: center; gap: 0.4rem; }
/* header-right: frei, ans rechte Ende geschoben. */
.hright { margin-left: auto; display: flex; align-items: center; gap: 0.5rem; }
`;

// Slotted-Wrapper nur zeigen, wenn befuellt — sonst frisst der head-gap Platz.
function hideWhenEmpty(wrap: HTMLElement, slot: HTMLSlotElement): void {
  const sync = () => {
    wrap.style.display = slot.assignedNodes({ flatten: true }).length ? "" : "none";
  };
  slot.addEventListener("slotchange", sync);
  sync();
}

defineComponent({
  tagName: "pfs-card-header",
  mixins: [withShadow()],
  render(host, ctx) {
    if (!ctx.shadowRoot) return;
    const root = ctx.shadowRoot;
    const style = document.createElement("style");
    style.textContent = STYLES;
    root.appendChild(style);

    const head = document.createElement("div");
    head.className = "head";

    const iconWrap = document.createElement("span");
    iconWrap.className = "ico";
    iconWrap.innerHTML = `<slot name="icon"></slot>`;

    const txt = document.createElement("span");
    txt.className = "txt";
    txt.innerHTML = `<h2 class="title"></h2><p class="subtitle"></p>`;
    const titleEl = txt.querySelector(".title") as HTMLElement;
    const subtitleEl = txt.querySelector(".subtitle") as HTMLElement;

    const hleft = document.createElement("span");
    hleft.className = "hleft";
    hleft.innerHTML = `<slot name="header-left"></slot>`;

    const hright = document.createElement("div");
    hright.className = "hright";
    hright.innerHTML = `<slot name="header-right"></slot>`;

    head.append(iconWrap, txt, hleft, hright);
    root.append(head);

    // PFS-760 (F1 Header-Jitter): hideWhenEmpty ERST NACH root.append aufrufen —
    // vorher ist der <slot> noch nicht im Shadow-DOM, assignedNodes()=leer → die
    // Icon-Kachel startet display:none und poppt per async slotchange nach
    // (sichtbarer Sprung beim Aufbau). Nach dem Append ist die Slot-Zuweisung
    // synchron korrekt → das initiale sync() sieht das Icon sofort, kein Jitter.
    hideWhenEmpty(iconWrap, iconWrap.querySelector("slot") as HTMLSlotElement);
    hideWhenEmpty(hleft, hleft.querySelector("slot") as HTMLSlotElement);
    hideWhenEmpty(hright, hright.querySelector("slot") as HTMLSlotElement);

    const paint = () => {
      titleEl.textContent = host.dataset.title ?? "";
      subtitleEl.textContent = host.dataset.subtitle ?? "";
    };
    paint();
    const obs = new MutationObserver(paint);
    ctx.cleanup.push(() => obs.disconnect());
    obs.observe(host, {
      attributes: true,
      attributeFilter: ["data-title", "data-subtitle"],
    });
  },
});
